/* <radar-making>: four clips from the workshop. Nothing downloads until the
   section is near the viewport; from there, the pointer (after a short
   delay), a tap, a swipe and the keyboard focus each choose a clip, and only
   one plays at a time. Pressing a tile never turns it off — it restarts the
   clip that is already playing, or starts the one that is not. Reduced motion
   turns every autoplay off and gives each tile its own explicit start/stop
   control instead.

   Below 700px the grid becomes a horizontal, snap-scrolling strip (its own
   CSS media query): the strip picks a clip by scroll position instead of by
   hover, through a second IntersectionObserver rooted in the strip itself
   rather than the viewport — the usual "am I on screen" question, just asked
   of the scroller instead of the window. */
(function (global) {
  'use strict';

  var reducedMotion = global.matchMedia ? global.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  // Mirrors the CSS breakpoint (assets/radar-making.css, `min-width: 700px`):
  // a live query, not a one-time read, so a browser window resized across it
  // (a laptop, not only a phone) is picked up without a page reload.
  var narrowQuery = global.matchMedia ? global.matchMedia('(max-width: 699.98px)') : { matches: false };

  if (typeof HTMLElement !== 'function') return;

  var HOVER_DELAY = 180;
  var STRIP_THRESHOLD = 0.6; // how much of a card must show to count as "the one in view"

  var RadarMaking = class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.grid = this.querySelector('.radar-making__grid');
      this.items = Array.prototype.slice.call(this.querySelectorAll('.radar-making__item'));
      this.tiles = this.items.map((item) => item.querySelector('.radar-making__tile'));
      this.videos = this.tiles.map((tile) => tile.querySelector('video'));
      this.verbs = this.tiles.map((tile) => tile.querySelector('.visually-hidden'));
      this.states = this.tiles.map((tile) => tile.querySelector('.radar-making__statetxt'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.radar-making__dot'));

      this.live = -1; // which clip is playing, -1 = none
      this.inView = false;
      this.stopped = false; // the visitor turned it off: do not restart on our own
      this.hoverTimer = null;

      this.tiles.forEach((tile, i) => this.wire(tile, i));

      // aria-pressed is only honest under reduced motion (see mark()): a
      // tile nobody has played yet still carries the Liquid markup's initial
      // aria-pressed="false" otherwise, so it is stripped up front too, not
      // only from the tiles a play()/stop() call happens to touch.
      if (!this.reduced()) this.tiles.forEach((tile) => tile.removeAttribute('aria-pressed'));

      // Si guarda la GRIGLIA, non la sezione, e a un terzo e non al 15%.
      //
      // Guardando la sezione al 15% si faceva partire la clip quando era ancora
      // sotto il bordo dello schermo: misurato su un telefono da 844 px, la
      // clip stava da 1020 a 1547, cioe' zero pixel visibili. Chrome la fa
      // partire lo stesso; Safari su iPhone si rifiuta di avviare da solo un
      // video che non e' in pagina, e nessuno riprovava piu' perche' per il
      // codice quella clip risultava gia' in riproduzione. Restava ferma sulla
      // locandina mentre la scheda diceva che stava andando.
      //
      // La griglia contiene le clip e nient'altro: quando un terzo di lei si
      // vede, la clip e' davvero sullo schermo, e parte dove ci si aspetta che
      // parta - quando la guardi.
      var watched = this.grid || this;
      if ('IntersectionObserver' in global) {
        this.observer = new global.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.enter();
            else this.leave();
          });
        }, { threshold: 0.34 });
        this.observer.observe(watched);
      } else {
        this.enter();
      }

      // The strip's own observer: root is the scroller, not the window, or
      // every card in a four-up grid would count as "in view" at once. It
      // stays harmless above 700px (the callback bails out) rather than being
      // torn down and rebuilt on every resize.
      //
      // An element-rooted observer clips the intersection rectangle against
      // its own overflow only, never against the outer viewport: the card at
      // scroll position 0 always reads back "intersecting" here, even while
      // the whole section sits three screens below the fold. choose() (not
      // play() directly) is what keeps that first, unearned notification from
      // doing anything — it checks inView, and it is also the same delay a
      // fast flick needs (below).
      if ('IntersectionObserver' in global && this.grid) {
        this.stripObserver = new global.IntersectionObserver((entries) => {
          if (this.reduced() || !this.narrow()) return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            var i = this.items.indexOf(entry.target);
            // Same delay as the pointer: a fast flick from card 1 to card 4
            // crosses the threshold of every card in between, and none of
            // them should start downloading just because the finger passed
            // over them on the way.
            if (i > -1) this.choose(i);
          });
        }, { root: this.grid, threshold: STRIP_THRESHOLD });
        this.items.forEach((item) => this.stripObserver.observe(item));
      }
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.stripObserver) this.stripObserver.disconnect();
      this.stripObserver = null;
    }

    wire(tile, i) {
      tile.addEventListener('click', () => {
        clearTimeout(this.hoverTimer);
        this.press(i);
      });
      tile.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
        this.choose(i);
      });
      tile.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer));
      tile.addEventListener('focus', () => {
        this.choose(i);
        // The strip can put a focused tile partly or fully off-screen: bring
        // it into view, or a keyboard visitor ends up on something they
        // cannot see. `block: 'nearest'` keeps the page itself from jumping.
        if (this.narrow() && typeof tile.scrollIntoView === 'function') {
          tile.scrollIntoView({ inline: 'center', block: 'nearest' });
        }
      });
      tile.addEventListener('blur', () => clearTimeout(this.hoverTimer));
      this.videos[i].addEventListener('ended', () => {
        if (this.live === i) {
          this.stop(i);
          this.live = -1;
        }
      });
    }

    reduced() {
      return reducedMotion.matches;
    }

    narrow() {
      return narrowQuery.matches;
    }

    // At full motion the tag only reports what is happening (pressing it
    // restarts the clip). Under reduced motion the tile is a real stop
    // control too, and both the tag and the accessible name say so.
    label(i, on) {
      var reduced = this.reduced();
      this.states[i].textContent = on ? (reduced ? this.dataset.labelStateStop : this.dataset.labelStateLive) : this.dataset.labelStateStart;
      this.verbs[i].textContent = on && reduced ? this.dataset.labelStopClip : this.dataset.labelPlayClip;
    }

    mark(i, on) {
      this.items[i].classList[on ? 'add' : 'remove']('is-live');
      // aria-pressed promises a real toggle: press it again and it releases.
      // That is only true under reduced motion (press stops the clip it just
      // started). At full motion pressing a live tile restarts it, it never
      // turns it off, so the attribute is dropped there instead of
      // announcing "pressed" on a control that will not let go.
      if (this.reduced()) this.tiles[i].setAttribute('aria-pressed', String(on));
      else this.tiles[i].removeAttribute('aria-pressed');
      this.label(i, on);
      // On the strip, "live" and "the card the scroll position is on" are the
      // same clip, so the dot reuses this instead of tracking scroll on its
      // own. It is decorative (aria-hidden): nothing here reads it back.
      if (this.dots[i]) this.dots[i].classList[on ? 'add' : 'remove']('is-active');
    }

    // Stopping: pause() alone does not cancel a download in progress — the
    // element keeps its src and the browser keeps filling the buffer behind
    // it. Removing the src before load() is what actually cancels the
    // fetch, so a card that stops being live also stops costing anything,
    // whether it lost its turn to another card or the visitor closed it.
    stop(i) {
      var video = this.videos[i];
      video.pause();
      if (video.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
      this.mark(i, false);
    }

    // The single choke point that ever writes `src`: every path that might
    // start a clip funnels through here, seen or not, so this is where being
    // off screen — or the merchant having emptied the blocks — has to stop
    // it, not in whichever caller happens to ask. An element-rooted
    // IntersectionObserver's very first notification can fire "intersecting"
    // for a card that sits inside a strip three screens below the fold (see
    // the strip observer above), and a scheduled choose() can still land
    // after the section has scrolled back out of view; inView is the guard
    // for both.
    play(i) {
      if (!this.inView) return;
      var video = this.videos[i];
      if (!video || this.live === i) return;
      if (this.live > -1) this.stop(this.live);
      this.live = i;
      this.stopped = false;

      if (!video.getAttribute('src')) video.setAttribute('src', video.getAttribute('data-src')); // <- the only place a byte is requested
      video.muted = true; // set again from script too, or iOS refuses to play
      video.loop = !this.reduced(); // no looping under reduced motion
      this.mark(i, true);

      var played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(() => {
          // The browser said no: the poster stays, and the tile says so.
          this.mark(i, false);
          if (this.live === i) this.live = -1;
        });
      }
    }

    // Pressing always means "start this one": on the tile that is already
    // playing it restarts from the top, it never turns it off. Reduced motion
    // is the one case where the tile is the sole control and a press has to
    // be able to close what it opened.
    press(i) {
      if (this.live !== i) {
        this.play(i);
        return;
      }
      if (this.reduced()) {
        this.stop(i);
        this.live = -1;
        this.stopped = true;
      } else {
        this.videos[i].currentTime = 0;
      }
    }

    // The pointer and the keyboard focus choose a clip, with a delay: sweeping
    // across the row should not pull four videos down at once.
    choose(i) {
      if (this.reduced() || !this.inView || this.live === i) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => this.play(i), HOVER_DELAY);
    }

    enter() {
      this.inView = true;
      if (this.reduced()) return;
      if (this.live > -1) {
        var played = this.videos[this.live].play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else if (!this.stopped) {
        this.play(0);
      }
    }

    leave() {
      this.inView = false;
      if (this.live > -1) this.videos[this.live].pause(); // off-screen spends nothing
    }
  };

  if (!customElements.get('radar-making')) customElements.define('radar-making', RadarMaking);
})(typeof window !== 'undefined' ? window : globalThis);
